import { z } from 'zod';
import { DateString, LooseDate, Uuid } from './common.js';
import { FactType, Sensitivity, Severity, SubjectStatus } from './vocab.js';

/**
 * The single-subject network read — `GET /orgs/:orgId/subjects/:id/network`.
 *
 * Demo-grade scope, not the stage-2 graph (design.md:15): one hop from the
 * subject plus the closure among that hop's nodes, and nothing deeper. Every
 * edge is an active fact from the record; the subject may sit in either fact
 * column (org-principal displacement puts an org in the subject slot with the
 * person as counterparty), so `a`/`b` are the fact's two entity columns as
 * stored and carry no direction. Direction for pair facts lives in `label`.
 *
 * Negated facts and wrong-person skeletons (`fields IS NULL`) are excluded
 * here, on purpose: the graph is a picture of what the record currently holds,
 * not a ledger of what it has considered. The record view stays the place that
 * shows removals with their reason.
 *
 * **Why an edge carries `role`, `window`, `note` and `last_corroborated`.**
 * Clicking a line asks *what is this, and when* — and for a closure edge that
 * question has nowhere else to go. A closure edge is a tie between two of the
 * subject's neighbours (a spouse's own board seat), so it appears in no subject
 * read at all: neither endpoint is the subject, and asking the record for it
 * would mean opening a dossier the reader did not ask for and may not be
 * entitled to. These four are the smallest answer that works for both kinds of
 * edge, they are read off the same current version the label already comes
 * from, and they cost the read nothing — no per-fact query is added for them
 * (`subject-network.service.ts` says why that constraint is load-bearing). What
 * they are deliberately *not* is the record row: no corroboration count, no
 * pending adjudication, no negation history. A reader who wants the fact wants
 * the record, and the panel's job is to get them there.
 *
 * **Why a node carries `status`.** "Who on this graph is a member" is a question
 * the picture exists to answer, and until it does the reader has to open every
 * circle to find out (statuses design note §1.8). It is the one thing about a
 * node that is not a property of the entity but of *this org's relationship to
 * it*, which is why it is null on every org and event node and on a person the
 * org does not hold a subject record for — a spouse the pipeline minted is a
 * node, never a subject, and absence there is the honest answer rather than a
 * missing row.
 */
export const NetworkNodeKind = z.enum(['person', 'org', 'event']);
export type NetworkNodeKind = z.infer<typeof NetworkNodeKind>;

export const NetworkNode = z.object({
  entity_id: Uuid,
  /** Live display name (tombstone/redirect-aware). Null when the entity was deleted. */
  display_name: z.string().nullable(),
  kind: NetworkNodeKind,
  is_subject: z.boolean(),
  unresolved: z.boolean().optional(),
  /**
   * What this person is to the org, in the org's vertical's own word. Null for
   * org and event nodes, and for a person who is not a subject of this org —
   * see the note above. The word is a vocabulary, not a label: what to print
   * for it is keyed by `org.vertical` on the client.
   */
  status: SubjectStatus.nullable(),
});
export type NetworkNode = z.infer<typeof NetworkNode>;

export const NetworkEdge = z.object({
  lineage_id: Uuid,
  type: FactType,
  /** The fact's `subject_entity_id` — an identity, not a direction. */
  a: Uuid,
  /** The fact's `counterparty_entity_id`. */
  b: Uuid,
  /**
   * Short human label. Pair facts phrase the relation from `a`'s side using the
   * direction in `fields` ("married to", "parent of"); counterparty types use
   * the type's plain word ("employment", "board seat"), or a direction word
   * where the payload establishes one ("donor", when `recipient` names `b`).
   */
  label: z.string(),
  sensitivity: Sensitivity,
  severity: Severity,
  /**
   * The capacity the tie was held in, for the three types that name one:
   * `employment.role`, `board_seat.role`, `event_participation.role`. Null for
   * every other type, and for a payload of those three that carries no role.
   */
  role: z.string().nullable(),
  /**
   * When the tie held. The windowed types (`employment`, `board_seat`,
   * `association_membership`) give both bounds — the same fields the record
   * read's window comes from, so the two never disagree. The types whose only
   * date is an opening (`social_tie.since`, `philanthropy.since`,
   * `financial_event.date`) give a start with a null end, which says "from
   * then", not "ended at an unknown time". Null — rather than a pair of nulls —
   * whenever neither bound is present, so a reader never renders a range the
   * record does not hold.
   *
   * Bounds are `LooseDate`. A roster that dated a seat `2021` said a year, and
   * the edge carries the year — the graph's panel is where "since 2021" is read
   * aloud, so it is the last place a fabricated month may be introduced.
   */
  window: z
    .object({
      start_date: LooseDate.nullable(),
      end_date: LooseDate.nullable(),
    })
    .nullable(),
  /**
   * A short phrase under the label, taken from whichever scalar of the payload
   * says what this tie actually was: `social_tie.context`, the cause and detail
   * of a gift, a degree and its year, an event's detail, a financial event's
   * kind and magnitude, `founder` on an employment. Never an entity reference —
   * the graph already draws both parties, and a name here would be a third one.
   */
  note: z.string().nullable(),
  /**
   * The lineage's newest supporting claim date — the record read's own
   * `last_corroborated`, so "last confirmed <date>" reads the same on the graph
   * as it does on the row. Null when every supporting claim is undated.
   */
  last_corroborated: DateString.nullable(),
});
export type NetworkEdge = z.infer<typeof NetworkEdge>;

/**
 * `GET /subjects/:id/network`.
 *
 * **`account_id` is an assertion rather than a filter** (security fold,
 * 2026-09-22). A subject's graph is drawn from that subject's own facts, and in
 * a security organization those only ever reach records of the same protection
 * account — resolution is account-scoped, so the wall is already drawn by the
 * time an edge exists. What the parameter buys is the same guarantee every other
 * read on the account chip gives: a graph asked for inside one engagement is not
 * quietly answered from another. A centre in a different account is `NOT_FOUND`,
 * exactly as the knowledge panel answers, and a vertical with no accounts
 * refuses the parameter outright.
 */
export const SubjectNetworkQuery = z.object({ account_id: Uuid.optional() }).strict();
export type SubjectNetworkQuery = z.infer<typeof SubjectNetworkQuery>;

export const NetworkResponse = z.object({
  subject_entity_id: Uuid,
  nodes: z.array(NetworkNode),
  edges: z.array(NetworkEdge),
});
export type NetworkResponse = z.infer<typeof NetworkResponse>;
